/**
 * useUnitTimezone
 * Resolves a unit's IANA timezone by ID via a batched lookup + long-lived cache,
 * so components can format timestamps correctly by unit ID alone — without
 * unit.state threaded through whatever payload happened to build the page (issue #910).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchUnitTimezones } from '../lib/portfolio.api';

const DAY_MS = 24 * 60 * 60 * 1000;

// Coalesces concurrent useUnitTimezone calls across the app, within one
// microtask tick, into a single POST /landlord/api/units/timezones request.
type PendingEntry = { resolve: (tz: string) => void; reject: (err: unknown) => void };
let pendingByUnitId = new Map<string, PendingEntry[]>();
let flushScheduled = false;

function flush(): void {
  const batch = pendingByUnitId;
  pendingByUnitId = new Map();
  flushScheduled = false;

  fetchUnitTimezones([...batch.keys()])
    .then((timezones) => {
      for (const [unitId, entries] of batch) {
        const tz = timezones[unitId];
        for (const entry of entries) {
          if (tz) {
            entry.resolve(tz);
          } else {
            entry.reject(new Error(`No timezone found for unit ${unitId}`));
          }
        }
      }
    })
    .catch((err: unknown) => {
      for (const entries of batch.values()) {
        for (const entry of entries) entry.reject(err);
      }
    });
}

function loadUnitTimezone(unitId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const entries = pendingByUnitId.get(unitId) ?? [];
    entries.push({ resolve, reject });
    pendingByUnitId.set(unitId, entries);
    if (!flushScheduled) {
      flushScheduled = true;
      queueMicrotask(flush);
    }
  });
}

/**
 * Resolves `unitId`'s IANA timezone. Timezone is effectively immutable per unit
 * (it only changes if a property is re-addressed across a zone boundary), so
 * results are cached for a day rather than refetched on every render.
 *
 * A missing/failed lookup surfaces as `isError` — never a silent browser-local
 * fallback — so callers decide how to degrade (e.g. hold a loading skeleton).
 */
export function useUnitTimezone(unitId: string | null | undefined): UseQueryResult<string> {
  return useQuery({
    queryKey: ['unit-timezone', unitId],
    queryFn: () => loadUnitTimezone(unitId as string),
    enabled: !!unitId,
    staleTime: DAY_MS,
    gcTime: DAY_MS,
    retry: 1,
  });
}
